import { TextField, IconButton, Zoom } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { FC, useState, KeyboardEvent, useContext, useEffect } from "react";
import { Search, Clear } from "@mui/icons-material";
import GridEventProvider from "components/context/GridEventContext";
import { PORTFOLIO_LAST_SEARCH_KEY } from "constant";
import dynamic from "next/dynamic";
import { Skeleton } from "@mui/material";
import { GridApi } from "ag-grid-community";

const useStyles = makeStyles(() => ({
  divider: {
    height: 32,
    margin: "0 8px",
  },
  searchBy: {
    fontWeight: 300,
    lineHeight: "16px",
    height: 16,
    width: 84,
    letterSpacing: 0.25,
    textAlign: "right",
  },
  searchBar: {
    "& fieldset": {
      borderRadius: "20px",
    },
  },
}));

// interface SearchBarProps {
//   gridEvent?: GridReadyEvent;
// }

const SearchBar: FC<{ preAccountId?: string }> = ({ preAccountId }) => {
  const classes = useStyles();
  const { gridEvent } = useContext(GridEventProvider);
  const [criteria, setCriteria] = useState(() => {
    const saved = localStorage.getItem(PORTFOLIO_LAST_SEARCH_KEY);
    if (!!saved) {
      const { externalSearch } = JSON.parse(saved);
      if (externalSearch) {
        const [_include, ...text] = externalSearch.filter.split("%|%");
        return text.join("%|%");
      }
    }
    return "";
  });
  const [includeInactive, setIncludeInactive] = useState(() => {
    const saved = localStorage.getItem(PORTFOLIO_LAST_SEARCH_KEY);
    if (!!saved) {
      const { externalSearch } = JSON.parse(saved);
      if (externalSearch) {
        const [include] = externalSearch.filter.split("%|%");
        return include === "true" ? true : false;
      }
    }
    return false;
  });

  useEffect(() => {
    if (gridEvent) {
      gridEvent.api.addEventListener("filterChanged", (e: any) => {
        const api: GridApi = e.api;
        const { externalSearch, ..._model } = api.getFilterModel();
        if (externalSearch) {
          const [include, ...text] = externalSearch.filter.split("%|%");
          setCriteria(text.join(""));
          setIncludeInactive(include === "true");
        } else {
          setCriteria("");
          setIncludeInactive(false);
        }
      });
    }
  }, [gridEvent]);

  useEffect(() => {
    if (gridEvent && preAccountId) {
      setCriteria(preAccountId);

      const filterModel = gridEvent?.api.getFilterModel();
      gridEvent?.api.setFilterModel({
        ...filterModel,
        externalSearch: {
          type: "contains",
          filter: `${includeInactive}%|%${preAccountId}`,
        },
      });
    }
  }, [gridEvent, preAccountId]);

  // when toggle change, submit
  useEffect(() => {
    handleSubmit();
  }, [includeInactive]);

  const handleChange = (type: "criteria" | "toggle") => (e: any) => {
    switch (type) {
      case "criteria":
        setCriteria(e.target.value);
        break;
      case "toggle":
        setIncludeInactive((old) => !old);
        break;
    }
    // gridEvent?.api.setFilterModel({
    //   ...gridEvent?.api.getFilterModel(),
    //   externalSearch: {
    //     type: "contains",
    //     filter: `${includeInactive}%|%${e.target.value.trim()}`,
    //   },
    // });
  };

  const handleSubmit = () => {
    if (!criteria) return;
    const trimmedCriteria = criteria.trim();
    setCriteria(trimmedCriteria);

    const filterModel = gridEvent?.api.getFilterModel();
    gridEvent?.api.setFilterModel({
      ...filterModel,
      externalSearch: {
        type: "contains",
        filter: `${includeInactive}%|%${trimmedCriteria}`,
      },
    });
  };

  const handleDelete = () => {
    setCriteria("");

    const filterModel = gridEvent?.api.getFilterModel();
    gridEvent?.api.setFilterModel({
      ...filterModel,
      externalSearch: {
        type: "contains",
        filter: `${includeInactive}%|%`,
      },
    });
  };

  const handleEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  if (preAccountId) {
    return null;
  }

  return (
    <div style={{ display: "flex", alignItems: "baseline" }}>
      <TextField
        value={criteria}
        onChange={handleChange("criteria")}
        onKeyPress={handleEnter}
        variant="outlined"
        margin="dense"
        placeholder="Search by Account ID, Account Name or Site Name"
        classes={{ root: classes.searchBar }}
        InputProps={{
          endAdornment: (
            <>
              <Zoom in={!!criteria?.length}>
                <IconButton
                  size="small"
                  onClick={handleDelete}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </Zoom>
              <IconButton
                size="small"
                onClick={handleSubmit}
              >
                <Search fontSize="small" />
              </IconButton>
            </>
          ),
        }}
        inputProps={{
          style: {
            width: 303,
            padding: "0 0 0 10px",
          },
        }}
      />
    </div>
  );
};

export default dynamic(() => Promise.resolve(SearchBar), {
  ssr: false,
  loading: () => (
    <Skeleton
      variant="rectangular"
      width={600}
      height={30}
      style={{ paddingRight: 23 }}
    />
  ),
});
